import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Slider,
  Checkbox,
  FormControlLabel,
  Box,
  Alert,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Link } from "react-router-dom";
import { useApi } from "../../utils/api";
import { calculateTotalWithPaymentsPro } from "../../utils/slotPricing";

const CoachUpgradeModal = ({
  open,
  onClose,
  currentSlots,
  currentPaymentsPro,
  clientCount,
  subscriptionId,
  onSuccess,
}) => {
  const { updateCoachSubscription } = useApi();
  const minSlots = clientCount + 1;
  const [slots, setSlots] = useState(Math.max(currentSlots || 0, minSlots));
  const [paymentsPro, setPaymentsPro] = useState(!!currentPaymentsPro);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const { breakdown, total, paymentsProCost } = calculateTotalWithPaymentsPro(
    slots,
    paymentsPro
  );
  const isUpgrade = slots > (currentSlots || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!subscriptionId) return;

    setLoading(true);
    try {
      await updateCoachSubscription(subscriptionId, { slots, paymentsPro });
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || "Failed to upgrade. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose?.()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Upgrade Your Subscription</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            You need more slots to add this client. <br /> You currently have{" "}
            {currentSlots || 0} slots and {clientCount} clients.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upgrade now and you&apos;ll be prorated for this month. The full
            amount will take effect at your next billing cycle.
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2">
              Client slots: <strong>{slots}</strong>
            </Typography>
            <Slider
              value={slots}
              onChange={(_, v) => setSlots(Math.max(v, minSlots))}
              min={minSlots}
              max={100}
              valueLabelDisplay="auto"
              marks={[
                { value: 10, label: "10" },
                { value: 30, label: "30" },
                { value: 60, label: "60" },
                { value: 100, label: "100" },
              ]}
            />
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={paymentsPro}
                onChange={(e) => setPaymentsPro(e.target.checked)}
              />
            }
            label="Stripe / Payments Pro Add-On ($10/month)"
          />
          <Box sx={{ bgcolor: "grey.50", p: 2, borderRadius: 1, mt: 2 }}>
            {breakdown.map((row) => (
              <Typography key={row.range} variant="body2">
                {row.slots} slots in {row.range} × ${row.pricePerSlot} = $
                {row.cost}
              </Typography>
            ))}
            {paymentsProCost > 0 && (
              <Typography variant="body2">
                Payments Pro: ${paymentsProCost}
              </Typography>
            )}
            <Typography variant="h6" sx={{ mt: 1 }}>
              New total: ${total}/month
            </Typography>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button component={Link} to="/coach-subscription" onClick={onClose}>
            Manage subscription
          </Button>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            variant="contained"
            loading={loading}
            disabled={loading || !isUpgrade}
          >
            Upgrade
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CoachUpgradeModal;
